import { getCommonCopy } from '../i18n.js';

const html = String.raw;

const DEFAULT_COPY = {
  title: 'Download',
  subtitle: 'Format auswählen und Export starten.',
  formatLabel: 'Format',
  templateLabel: 'Vorlage',
  standardOption: 'Standard Export',
  manageTemplates: 'Vorlagen verwalten',
  noTemplates: 'Keine Vorlagen verfügbar.',
  cancel: 'Abbrechen',
  confirm: 'Download starten',
  loading: 'Download läuft…',
};

const normalizeTemplates = (templates) => (Array.isArray(templates) ? templates : []);

export const openDownloadOverlay = (options = {}) => {
  const commonCopy = options.commonCopy ?? getCommonCopy();
  const downloadCopy = commonCopy?.download ?? {};
  const copy = { ...DEFAULT_COPY, ...downloadCopy, ...(options.copy ?? {}) };

  const formats = Array.isArray(options.formats) ? options.formats : [];
  if (!formats.length) {
    console.warn('Download overlay: no formats provided');
    return () => {};
  }

  const title = options.title ?? copy.title;
  const subtitle = options.subtitle ?? copy.subtitle;
  const manageTemplatesHref = options.manageTemplatesHref ?? null;
  const templatesByFormat = options.templatesByFormat ?? {};
  const onConfirm = typeof options.onConfirm === 'function' ? options.onConfirm : null;

  let selectedFormat = options.defaultFormat ?? formats[0].id;
  let selectedTemplateId = null;
  let selectedTemplate = null;

  const overlay = document.createElement('div');
  overlay.className = 'download-overlay';
  overlay.innerHTML = html`
    <div class="download-overlay__backdrop" data-download-close></div>
    <div class="download-overlay__panel" role="dialog" aria-modal="true">
      <header class="download-overlay__header">
        <div>
          <h3 class="download-overlay__title">${title}</h3>
          <p class="download-overlay__subtitle">${subtitle}</p>
        </div>
        <button type="button" class="download-overlay__close" data-download-close aria-label="Close">✕</button>
      </header>
      <div class="download-overlay__section">
        <p class="download-overlay__label">${copy.formatLabel}</p>
        <div class="download-overlay__formats">
          ${formats
            .map(
              (format) => html`
                <button
                  type="button"
                  class="download-format-button ${format.id === selectedFormat ? 'active' : ''}"
                  data-download-format="${format.id}"
                >
                  <span class="download-format-title">${format.label}</span>
                  ${format.description ? `<span class="download-format-desc">${format.description}</span>` : ''}
                </button>
              `,
            )
            .join('')}
        </div>
      </div>
      <div class="download-overlay__section">
        <div class="download-overlay__templates-header">
          <p class="download-overlay__label">${copy.templateLabel}</p>
          ${manageTemplatesHref ? `<a class="download-overlay__manage" href="${manageTemplatesHref}">${copy.manageTemplates} →</a>` : ''}
        </div>
        <div class="download-overlay__templates" data-download-templates></div>
      </div>
      <div class="download-overlay__footer">
        <p class="download-overlay__status muted small" data-download-status></p>
        <div class="download-overlay__actions">
          <button type="button" class="ghost-button" data-download-cancel>${copy.cancel}</button>
          <button type="button" class="button" data-download-confirm>${copy.confirm}</button>
        </div>
      </div>
    </div>
  `;

  document.body.appendChild(overlay);

  const formatButtons = Array.from(overlay.querySelectorAll('[data-download-format]'));
  const templateContainer = overlay.querySelector('[data-download-templates]');
  const statusEl = overlay.querySelector('[data-download-status]');
  const confirmButton = overlay.querySelector('[data-download-confirm]');
  const cancelButton = overlay.querySelector('[data-download-cancel]');
  const closeButtons = overlay.querySelectorAll('[data-download-close]');

  const getFormat = () => formats.find((format) => format.id === selectedFormat) ?? formats[0];

  const resolveTemplates = (format) => {
    const key = format.templateFormat ?? format.id;
    return normalizeTemplates(templatesByFormat[key]);
  };

  const updateConfirmState = () => {
    const format = getFormat();
    const requiresTemplate = !!format.requiresTemplate;
    const missingTemplate = requiresTemplate && !selectedTemplateId;
    if (confirmButton) {
      confirmButton.disabled = missingTemplate || !onConfirm;
    }
  };

  const renderTemplates = () => {
    const format = getFormat();
    const templates = resolveTemplates(format);
    const allowStandard = !format.requiresTemplate;
    const defaultTemplate = templates.find((template) => template.isDefault) || templates[0] || null;

    const isTemplateSelected =
      selectedTemplateId && templates.some((template) => template.templateId === selectedTemplateId);

    if (format.requiresTemplate) {
      if (!isTemplateSelected) {
        selectedTemplateId = defaultTemplate?.templateId ?? null;
      }
    } else if (selectedTemplateId === null || (selectedTemplateId && !isTemplateSelected)) {
      selectedTemplateId = '';
    }

    selectedTemplate = templates.find((template) => template.templateId === selectedTemplateId) ?? null;

    const templateRows = [];

    if (allowStandard) {
      templateRows.push(`
        <button type="button" class="download-template-button ${selectedTemplateId === '' ? 'active' : ''}" data-download-template="">
          <span>${copy.standardOption}</span>
        </button>
      `);
    }

    if (templates.length) {
      templates.forEach((template) => {
        const isActive = template.templateId === selectedTemplateId;
        templateRows.push(`
          <button type="button" class="download-template-button ${isActive ? 'active' : ''}" data-download-template="${template.templateId}">
            <span>${template.name}</span>
            ${template.isDefault ? '<span class="download-template-badge">★</span>' : ''}
          </button>
        `);
      });
    } else if (!allowStandard) {
      templateRows.push(`<p class="muted small">${copy.noTemplates}</p>`);
    }

    templateContainer.innerHTML = templateRows.join('');
    templateContainer.querySelectorAll('[data-download-template]').forEach((button) => {
      button.addEventListener('click', () => {
        selectedTemplateId = button.dataset.downloadTemplate ?? '';
        selectedTemplate = templates.find((t) => t.templateId === selectedTemplateId) ?? null;
        renderTemplates();
      });
    });

    updateConfirmState();
  };

  const close = () => {
    overlay.remove();
    document.removeEventListener('keydown', handleKeydown);
  };

  const handleKeydown = (event) => {
    if (event.key === 'Escape') {
      close();
    }
  };

  document.addEventListener('keydown', handleKeydown);

  closeButtons.forEach((button) => {
    button.addEventListener('click', close);
  });

  if (cancelButton) {
    cancelButton.addEventListener('click', close);
  }

  if (confirmButton) {
    confirmButton.addEventListener('click', async () => {
      if (!onConfirm) return;
      if (statusEl) statusEl.textContent = '';
      confirmButton.disabled = true;
      const previousLabel = confirmButton.textContent;
      confirmButton.textContent = copy.loading;
      try {
        await onConfirm({
          format: selectedFormat,
          templateId: selectedTemplateId || null,
          template: selectedTemplate,
        });
        close();
      } catch (error) {
        if (statusEl) {
          statusEl.textContent = error instanceof Error ? error.message : 'Download fehlgeschlagen.';
        }
        confirmButton.disabled = false;
      } finally {
        confirmButton.textContent = previousLabel;
        updateConfirmState();
      }
    });
  }

  formatButtons.forEach((button) => {
    button.addEventListener('click', () => {
      selectedFormat = button.dataset.downloadFormat ?? formats[0].id;
      selectedTemplateId = null;
      selectedTemplate = null;
      formatButtons.forEach((btn) => {
        btn.classList.toggle('active', btn.dataset.downloadFormat === selectedFormat);
      });
      renderTemplates();
    });
  });

  renderTemplates();

  return close;
};
